import * as React from 'react';
import classNames from 'classnames';
import { MediaSet } from '@/types/media';
import { Button } from '../Button';
import { Image } from '../Image';
import { Link } from '../Link';
import { LoadingBar } from '../LoadingBar';
import { TextBadge } from '../TextBadge';
import styles from './FeaturedArticle.module.scss';

/** The design shows at most three labels, the same cap the grid cards use */
const maxLabels = 3;

export interface FeaturedArticleProps {
	readonly className?: string;
	/** Renders the card as a placeholder, so the page keeps its shape while loading */
	readonly isSkeleton?: boolean;
	readonly image?: MediaSet;
	readonly altText?: string;
	readonly url?: string;
	/** Shown above the headline, the same card labels the grid cards carry */
	readonly labels?: readonly string[];
	readonly headline: string;
	readonly description?: string;
	readonly authorName?: string;
	readonly authorImage?: MediaSet;
	/** Already formatted for the current culture */
	readonly publicationDate?: string;
	/** Free text from the CMS, e.g. "3 min" */
	readonly readingTime?: string;
	readonly readArticleLabel: string;
	/** Spoken instead of the headline alone, so the link says what it leads to */
	readonly linkAriaLabel?: string;
	readonly lazyLoading?: boolean;
}

/**
 * Generic FeaturedArticle component, used to lift one article above an overview,
 * e.g. at the top of the Knowledge Hub.
 *
 * The text sits on top of the image, so every label is white and the image carries
 * a gradient dark enough to keep the text readable whatever the photograph shows.
 */
export const FeaturedArticle: React.FunctionComponent<FeaturedArticleProps> = ({
	className,
	isSkeleton,
	image,
	altText,
	url,
	labels,
	headline,
	description,
	authorName,
	authorImage,
	publicationDate,
	readingTime,
	readArticleLabel,
	linkAriaLabel,
	lazyLoading,
}) => {
	// Skipping the empty ones keeps a missing author or date from leaving a stray separator
	const metaText = [authorName, publicationDate, readingTime].filter(Boolean).join(' | ');
	const visibleLabels = labels?.slice(0, maxLabels) ?? [];

	if (isSkeleton) {
		return (
			<article className={classNames(styles.wrapper, styles.wrapperSkeleton, className)} aria-hidden={'true'}>
				<div className={styles.content}>
					<div className={styles.text}>
						<LoadingBar width={'120px'} height={'24px'} />
						<LoadingBar width={'80%'} height={'48px'} />
						<LoadingBar width={'40%'} height={'16px'} />
						<LoadingBar width={'90%'} height={'25px'} />
					</div>
					<LoadingBar width={'160px'} height={'48px'} />
				</div>
			</article>
		);
	}

	return (
		<article className={classNames(styles.wrapper, className)}>
			<div className={styles.imageWrapper}>
				<Image
					className={styles.image}
					{...image}
					responsiveSizesSettings={'100vw'}
					altText={image?.altText || altText || ''}
					lazyLoading={lazyLoading}
				/>
				<div className={styles.overlay} />
			</div>
			<div className={styles.content}>
				<div className={styles.text}>
					{visibleLabels.length > 0 && (
						<ul className={styles.labels}>
							{visibleLabels.map((label) => (
								<li key={label}>
									<TextBadge className={styles.label} text={label} />
								</li>
							))}
						</ul>
					)}
					<p className={styles.headline}>
						{/* The link stretches across the card, so the whole card is clickable
						    while staying a single link named after the article */}
						<Link
							className={styles.headlineLink}
							linkUrl={url || ''}
							target={'_self'}
							aria-label={linkAriaLabel || undefined}
						>
							{headline}
						</Link>
					</p>
					{metaText && (
						<div className={styles.authorInfo}>
							{authorImage && (
								<Image
									className={styles.avatar}
									{...authorImage}
									responsiveSizesSettings={'24px'}
									altText={''}
									lazyLoading={lazyLoading}
								/>
							)}
							<p className={styles.meta}>{metaText}</p>
						</div>
					)}
					{description && <p className={styles.description}>{description}</p>}
				</div>
				{/* Decorative: the stretched headline link already makes the card clickable */}
				<Button className={styles.cta} variant={'outline'} aria-hidden={'true'} tabIndex={-1}>
					{readArticleLabel}
				</Button>
			</div>
		</article>
	);
};
